import * as React from 'react';
import { CSSProperties } from 'react';
import { MenuItemArrowIcon, MenuItemCheckIcon } from '../../icons/Icons';
import { PreventUserSelect } from '../../styles/PreventUserSelect';
import { TabStyles } from '../../styles/TabStyles';
import { TextEllipsis } from '../../styles/TextEllipsis';
import { conditional, makeTestId } from '../../utils/Declarative';
import { isElementOverflownX } from '../../utils/DomUtil';
import { Img } from '../DomWidgets';
import { MenuItemCheckStyles } from '../List/ListStyles';
import { DisplayValueFormatterProps } from './DisplayValueFormatter';

const { Sizing, Colors, Spacing, Typography } = TabStyles;

const getStyles: () => {
  MenuItem: CSSProperties,
  GroupLabel: CSSProperties,
  Highlighted: CSSProperties,
  Active: CSSProperties,
  WithCheckGutter: CSSProperties,
  WithSubMenu: CSSProperties,
  Disabled: CSSProperties,
  ArrowIcon: CSSProperties
} = () => {
  return {
    MenuItem: {
      height: Sizing.ListRowHeight,
      paddingLeft: Spacing.DefaultSpace,
      paddingRight: Spacing.DefaultSpace,
      lineHeight: Sizing.ListRowHeight + 'px',
      position: 'relative',
      ...TextEllipsis,
      ...PreventUserSelect
    },
    GroupLabel: {
      fontWeight: 'bold',
      height: TabStyles.Sizing.ListRowHeight,
      lineHeight: TabStyles.Sizing.ListRowHeight + 'px',
      ...TextEllipsis,
      ...PreventUserSelect,
    },
    Highlighted: {
      backgroundColor: Colors.F2
    },
    Active: {
      backgroundColor: Colors.F3
    },
    WithCheckGutter: {
      paddingLeft: MenuItemCheckIcon.checkedItemPaddingLeft
    },
    WithSubMenu: {
      paddingRight: MenuItemArrowIcon.width
    },
    Disabled: {
      color: Typography.Color.Disabled
    },
    ArrowIcon: {
      position: 'absolute',
      right: 0
    }
  };
};

interface TextFormatterState {
  title?: string;
}

/**
 * Using closure the formatter has access to the text value that it wants to display, and gets its other props
 * from the dropdown. This will be rendered in the MenuItemWidget.
 *
 * @param displayValue The text value to be displayed
 */
export const TextMenuDisplayFormatter = (displayValue?: string, title?: string): React.ComponentClass<DisplayValueFormatterProps> => {
  return class TextFormatterWidget extends React.Component<DisplayValueFormatterProps, TextFormatterState> {
    public constructor(props: DisplayValueFormatterProps) {
      super(props);
      this.state = { title: title };
    }

    private handleMouseOver = (e: React.MouseEvent<HTMLDivElement>): void => {
      if (title) {
        // NOTE: The custom title will override the default title when the displayed value within the menu item is truncated.
        return;
      }

      this.setState({ title: (isElementOverflownX(e.currentTarget)) ? displayValue : undefined });
    }

    private getSubMenuIcon(): React.ReactNode {
      if (this.props.hasSubMenu) {
        const styles = getStyles();
        const arrowIcon = MenuItemArrowIcon.icon({ disabled: this.props.disabled });

        return <Img style={styles.ArrowIcon} src={arrowIcon} aria-hidden={true} />;
      } else {
        return null;
      }
    }
    public render(): JSX.Element {
      const styles = getStyles();

      const style = this.props.isGroupLabel ? styles.GroupLabel : {
        ...styles.MenuItem,
        ...conditional(this.props.hasSubMenu, styles.WithSubMenu),
        ...conditional(this.props.isHighlighted, styles.Highlighted),
        ...conditional(this.props.active, styles.Active),
        ...conditional(this.props.disabled, styles.Disabled),
        ...conditional(this.props.showCheckGutter, styles.WithCheckGutter),
        ...conditional(this.props.checked, MenuItemCheckStyles({
          active: this.props.active || this.props.isHighlighted,
          disabled: this.props.disabled,
        })),
      };

      return (
        <div
          data-tb-test-id={makeTestId(this.props.testId, 'TextMenuItem')}
          onMouseOver={this.handleMouseOver}
          style={style}
          title={this.state.title}
        >
          {displayValue}
          {this.getSubMenuIcon()}
        </div>
      );
    }
  };
};
